// WARNING: PRELOAD_MAP import paths must stay in sync with src/routes/index.tsx.
// Two-way drift here will cause chunk duplication (separate bundles instead of shared).
import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCinematicNavigate } from "@/hooks/useCinematicNavigate";
import { useCinematicContext } from "@/components/landing/CinematicTransitionProvider";
import type { Variant } from "@/lib/landing/cinematicTransitions";

// Preload map mirrors the lazyRetry() calls in src/routes/index.tsx exactly.
// WARNING: Keep in sync with src/routes/index.tsx — drift causes chunk duplication.
const PRELOAD_MAP: Record<string, () => Promise<unknown>> = {
  "/pricing": () => import("@/pages/PricingPage"),
  "/coverage": () => import("@/pages/CoveragePage"),
  "/docs": () => import("@/pages/DocsPage"),
  "/blog": () => import("@/pages/BlogIndex"),
  "/about": () => import("@/pages/AboutPage"),
  "/verify": () => import("@/pages/PublicCarrierLookup"),
  "/signup": () => import("@/pages/Signup"),
  "/login": () => import("@/pages/Login"),
  "/brokers/signup": () => import("@/pages/dash-v2/BrokerSignupPage"),
  // Landing CTAs preload their destination chunk on hover/focus so the
  // (plain, instant) navigation lands with the chunk already warm.
  // Specifiers mirror src/App.tsx exactly so the chunks are shared,
  // never duplicated.
  "/mcp": () => import("@/pages/MCPPage"),
  "/mcp/setup": () => import("@/pages/MCPSetupPage"),
  "/product/packet-scanner": () => import("@/pages/PackageScannerPage"),
  "/book-demo": () => import("@/pages/BookDemoPage"),
  // Specifier mirrors src/routes/index.tsx (`DemoVideoPage`) exactly so
  // the chunk is shared, never duplicated.
  "/demo-tour": () => import("@/pages/DemoVideoPage"),
};

// Track which routes have already been preloaded so we only fire once per session
const preloadedRoutes = new Set<string>();

interface CinematicLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  variant?: Variant;
  children: React.ReactNode;
  className?: string;
  "aria-current"?: React.AnchorHTMLAttributes<HTMLAnchorElement>["aria-current"];
}

export function CinematicLink({ to, variant, children, className, onClick, ...rest }: CinematicLinkProps) {
  const ctx = useCinematicContext();
  const navigate = useNavigate();
  const cinematicNavigate = useCinematicNavigate();
  const hasProvider = ctx !== undefined;

  const handleMouseEnterOrFocus = () => {
    const preload = PRELOAD_MAP[to];
    if (preload && !preloadedRoutes.has(to)) {
      preloadedRoutes.add(to);
      preload().catch(() => {
        // Idempotent — ignore preload failures
      });
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Pass through to onClick if provided
    onClick?.(e);

    // Only intercept plain left-clicks without modifier keys
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      return;
    }

    e.preventDefault();

    if (hasProvider) {
      cinematicNavigate(to, {
        variant,
        preload: PRELOAD_MAP[to],
        originPoint: { x: e.clientX, y: e.clientY },
      });
    } else {
      // Defensive fallback when provider is not mounted (e.g., tests without provider)
      navigate(to);
    }
  };

  return (
    <a
      href={to}
      className={className}
      onClick={handleClick}
      onMouseEnter={handleMouseEnterOrFocus}
      onFocus={handleMouseEnterOrFocus}
      {...rest}
    >
      {children}
    </a>
  );
}
